import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar, {
  readSidebarCollapsedPreference,
  writeSidebarCollapsedPreference,
} from '../components/Sidebar';
import MobileFooter from '../components/MobileFooter';
import MobileHeader from '../components/MobileHeader';
import './PrivateLayout.css';  // Le fichier CSS spécifique au layout privé
import '../styles/ios-fixes.css'; // Corrections iOS globales
import { useAuth } from '../context/AuthContext';
import {
  TwilioConversationsProvider,
  resolveTwilioProIdentity,
} from '../contexts/TwilioConversationsContext';
import { Drawer, IconButton, Typography, Box } from '@mui/material';
import {
  AccessTime,
  CalendarMonth,
  Close as CloseIcon,
  HomeOutlined,
  Menu,
  PersonOutline,
  Stop,
} from '@mui/icons-material';
// Onboarding désactivé : remplacé par bandeau sur AccueilPage + page /app/demarrage
import { Capacitor } from '@capacitor/core';
import DesktopHeader from '../components/DesktopHeader/DesktopHeader';
import AccountRestrictionModal from '../components/AccountRestrictionModal';
import WhatsNewEpic4Modal from '../components/WhatsNewEpic4/WhatsNewEpic4Modal';
import { RouteLazyFallback } from '../components/RouteSuspense/RouteSuspense';
import PushNotificationPrompt from '../components/PushNotificationPrompt';
import { AppIconBadgeSync } from '../hooks/useSyncAppIconBadge';
import { resolveOnboardingOpenSidebarAction } from '../utils/onboardingSidebar';

const PROGRAMMER_MENU_ITEMS = [
  { key: 'appointment', Icon: PersonOutline, labelKey: 'programMenu.clientAppointment' },
  { key: 'stay', Icon: HomeOutlined, labelKey: 'programMenu.stay' },
  { key: 'slots', Icon: CalendarMonth, labelKey: 'menu.registrationService' },
  { key: 'unavailabilities', Icon: Stop, labelKey: 'menu.unavailabilities' },
  { key: 'availabilities', Icon: AccessTime, labelKey: 'menu.serviceAvailabilities' },
] as const;

const PrivateLayout: React.FC = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['planning', 'sidebar']);
  const proIdentity = resolveTwilioProIdentity(user);
  const accountRestriction = user?.account_restriction;
  const isAccountLimited = Boolean(accountRestriction?.is_limited);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(
    () => window.innerWidth >= 768 && readSidebarCollapsedPreference()
  );
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [programmerDrawerOpen, setProgrammerDrawerOpen] = useState(false);
  const [headerPropsVersion, setHeaderPropsVersion] = useState(0);
  const [reservationBadgeCount, setReservationBadgeCount] = useState(0);
  const layoutRightRef = useRef<HTMLDivElement>(null);

  const toggleDesktopSidebar = useCallback(() => {
    setDesktopSidebarCollapsed((prev) => {
      const next = !prev;
      writeSidebarCollapsedPreference(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setDrawerVisible(false);
      } else {
        setDesktopSidebarCollapsed(readSidebarCollapsedPreference());
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      document.documentElement.style.removeProperty('--sidebar-width');
      return;
    }
    document.documentElement.style.setProperty(
      '--sidebar-width',
      desktopSidebarCollapsed ? '0px' : '220px'
    );
    return () => {
      document.documentElement.style.removeProperty('--sidebar-width');
    };
  }, [desktopSidebarCollapsed, isMobile]);

  // Écouter les changements de compteurs de réservations
  useEffect(() => {
    const handleReservationCounts = (event: any) => {
      if (event.detail && typeof event.detail.count === 'number') {
        setReservationBadgeCount(event.detail.count);
      }
    };

    window.addEventListener('reservationBadgeCountChanged', handleReservationCounts);
    return () => window.removeEventListener('reservationBadgeCountChanged', handleReservationCounts);
  }, []);

  // Détecter la plateforme Capacitor et ajouter les classes CSS appropriées
  useEffect(() => {
    const platform = Capacitor.getPlatform();
    const isNative = Capacitor.isNativePlatform();

    // Ajouter les classes au body pour les styles conditionnels
    if (isNative) {
      document.body.classList.add('capacitor-native');
      if (platform === 'ios') {
        document.body.classList.add('capacitor-ios');
      } else if (platform === 'android') {
        document.body.classList.add('capacitor-android');
      }
    }

    // Cleanup
    return () => {
      document.body.classList.remove('capacitor-native', 'capacitor-ios', 'capacitor-android');
    };
  }, []);

  // Fermer les drawers quand on change de page
  useEffect(() => {
    setDrawerVisible(false);
    setProgrammerDrawerOpen(false);
    // Reset badge count when navigating away from reservations if desired
    // (but usually we keep it if it's a global notification)
    if (!location.pathname.includes('/reservations')) {
      setReservationBadgeCount(0);
    } else {
      // Si on arrive sur les réservations, on demande au composant de renvoyer son compte si nécessaire
      window.dispatchEvent(new CustomEvent('requestReservationBadgeCount'));
    }
  }, [location.pathname]);

  // Remonter en haut à chaque changement de page (mobile: conteneur ; desktop: window).
  // Respecte les ancres (#…) comme sur PublicLayout.
  useEffect(() => {
    if (location.hash) return;
    const scroller = layoutRightRef.current;
    if (scroller) scroller.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith('/app')) {
      const fullPath = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
      localStorage.setItem('lastVisitedPrivatePath', fullPath);
    }
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!location.pathname.startsWith('/app')) return;
    if (!localStorage.getItem('token')) return;
    refreshUser().catch(() => {
      // La vérification de limitation ne doit pas casser la navigation si l'API est momentanément indisponible.
    });
  }, [location.pathname, location.search, refreshUser]);

  // Écouter les changements des props de planning
  useEffect(() => {
    const handlePlanningPropsChange = () => {
      setHeaderPropsVersion(prev => prev + 1);
    };

    window.addEventListener('planningPropsChanged', handlePlanningPropsChange);
    return () => window.removeEventListener('planningPropsChanged', handlePlanningPropsChange);
  }, []);

  // Sur mobile : ouvrir le drawer Programmer depuis n'importe quel onglet (bouton central)
  useEffect(() => {
    if (!isMobile) return;
    const onOpen = () => setProgrammerDrawerOpen(true);
    window.addEventListener('openProgrammerMenu', onOpen);
    return () => window.removeEventListener('openProgrammerMenu', onOpen);
  }, [isMobile]);

  const handleProgrammerAction = (key: string) => {
    setProgrammerDrawerOpen(false);
    const isMainPlanning =
      location.pathname === '/app/planning' || location.pathname === '/app/planning/';
    switch (key) {
      case 'appointment':
        navigate('/app/reservations?action=add_booking');
        break;
      case 'stay':
        navigate('/app/reservations?action=add_stay');
        break;
      case 'slots':
        // Même URL : la navigation avec state ne réouvre pas toujours les modales ; on émet un événement global.
        if (isMainPlanning) {
          window.dispatchEvent(new CustomEvent('openAddSlotsModal'));
        } else {
          navigate('/app/planning', { state: { openSlotsModal: true } });
        }
        break;
      case 'unavailabilities':
        if (isMainPlanning) {
          window.dispatchEvent(new CustomEvent('openAddUnavailabilityModal'));
        } else {
          navigate('/app/planning', { state: { openUnavailabilityModal: true } });
        }
        break;
      case 'availabilities':
        if (isMainPlanning) {
          window.dispatchEvent(new CustomEvent('openAddAvailabilityModal'));
        } else {
          navigate('/app/planning', { state: { openAvailabilityModal: true } });
        }
        break;
      default:
        break;
    }
  };

  /** Footer mobile : toujours le menu Programmer (rdv, séjour, créneaux, etc.) */
  const handleMobileFooterAddClick = () => {
    window.dispatchEvent(new CustomEvent('openProgrammerMenu'));
  };

  /** Header mobile « + » : même logique contextuelle que le desktop (forfaits, services, etc.) */
  const handleAddClick = () => {
    if (isMobile) {
      if (location.pathname.includes('/reservations')) {
        navigate('/app/reservations?action=add');
        return;
      }
      if (location.pathname.includes('/clients') || location.pathname.includes('/chiens')) {
        navigate('/app/clients?action=add');
        return;
      }
      if (location.pathname.includes('/services')) {
        navigate('/app/services?action=add');
        return;
      }
      if (location.pathname.includes('/forfaits')) {
        navigate('/app/forfaits?action=add');
        return;
      }
      if (location.pathname.includes('/credits')) {
        navigate('/app/credits?action=add');
        return;
      }
      if (location.pathname.includes('/cartes-cadeaux')) {
        navigate('/app/cartes-cadeaux?action=add');
        return;
      }
      if (location.pathname.includes('/abonnements')) {
        navigate('/app/abonnements?action=add');
        return;
      }
      if (location.pathname.includes('/planning')) {
        window.dispatchEvent(new CustomEvent('openProgrammerMenu'));
        return;
      }
      navigate('/app/reservations');
      return;
    }
    if (location.pathname.includes('/reservations')) {
      navigate('/app/reservations?action=add');
    } else if (location.pathname.includes('/clients') || location.pathname.includes('/chiens')) {
      navigate('/app/clients?action=add');
    } else if (location.pathname.includes('/services')) {
      navigate('/app/services?action=add');
    } else if (location.pathname.includes('/forfaits')) {
      navigate('/app/forfaits?action=add');
    } else if (location.pathname.includes('/credits')) {
      navigate('/app/credits?action=add');
    } else if (location.pathname.includes('/cartes-cadeaux')) {
      navigate('/app/cartes-cadeaux?action=add');
    } else if (location.pathname.includes('/abonnements')) {
      navigate('/app/abonnements?action=add');
    } else if (location.pathname.includes('/planning')) {
      window.dispatchEvent(new CustomEvent('openProgrammerMenu'));
      return;
    } else {
      navigate('/app/reservations');
    }
  };

  const handleFilterClick = () => {
    if (location.pathname.includes('/reservations')) {
      // Déclencher l'événement pour ouvrir le drawer de filtres
      window.dispatchEvent(new Event('mobile-header-filter'));
    }
  };

  const handleSearchClick = () => {
    if (location.pathname.includes('/clients') || location.pathname.includes('/chiens')) {
      // Déclencher l'événement pour ouvrir le drawer de recherche
      window.dispatchEvent(new Event('mobile-header-search'));
    }
  };

  const toggleDrawer = () => {
    setDrawerVisible(!drawerVisible);
  };

  useEffect(() => {
    const openSidebarDrawer = () => {
      const action = resolveOnboardingOpenSidebarAction(window.innerWidth);
      if (action === 'open-mobile-drawer') {
        setDrawerVisible(true);
      } else if (action === 'expand-desktop-sidebar') {
        // Desktop collapsed : #nav-help est visibility:hidden — expand sans écraser la préférence LS
        setDesktopSidebarCollapsed(false);
      }
    };
    const closeSidebarDrawer = () => {
      if (resolveOnboardingOpenSidebarAction(window.innerWidth) === 'open-mobile-drawer') {
        setDrawerVisible(false);
      }
    };

    window.addEventListener('onboarding:open-sidebar', openSidebarDrawer);
    window.addEventListener('onboarding:close-sidebar', closeSidebarDrawer);

    return () => {
      window.removeEventListener('onboarding:open-sidebar', openSidebarDrawer);
      window.removeEventListener('onboarding:close-sidebar', closeSidebarDrawer);
    };
  }, []);

  // Déterminer les actions à afficher dans le header selon la page
  const getHeaderProps = () => {
    const path = location.pathname;

    if (path.includes('/clients') || path.includes('/chiens')) {
      return { showSearch: true }; // Enlevé showAdd
    }
    if (path.includes('/reservations')) {
      return { showFilter: true, showAdd: true, badgeCount: reservationBadgeCount };
    }
    if (path.includes('/messages')) {
      return { showSearch: true };
    }
    if (path.includes('/services')) {
      return { showAdd: true };
    }
    if (path.includes('/forfaits')) {
      return { showAdd: true };
    }
    if (path.includes('/planning')) {
      // Récupérer les props de planning depuis le composant
      const planningProps = (window as any).planningHeaderProps || {};
      return planningProps;
    }

    return {};
  };

  const headerProps = getHeaderProps();
  // Force re-render when planning props change
  const _ = headerPropsVersion;

  // Ne pas monter Twilio avec identity vide (avant refreshUser / sans pro_id) : évite token invalide et erreurs SDK sur toute l'app privée.
  const layoutTree = (
      <div
        className={`private-layout ${isMobile ? 'mobile' : 'desktop'} ${!isMobile && desktopSidebarCollapsed ? 'sidebar-collapsed' : ''} ${isAccountLimited ? 'account-limited' : ''}`}
      >
        <AppIconBadgeSync />
        {/* Header mobile avec MobileHeader */}
        {isMobile && !isAccountLimited && (
          <MobileHeader
            onMenuClick={toggleDrawer}
            onAddClick={handleAddClick}
            onFilterClick={handleFilterClick}
            onSearchClick={handleSearchClick}
            {...headerProps}
          />
        )}

        {/* Sidebar desktop / Drawer mobile */}
        {!isAccountLimited && !isMobile && desktopSidebarCollapsed && (
          <button
            type="button"
            className="sidebar-reopen-fab"
            data-rtt-sidebar-collapse="reopen"
            onClick={toggleDesktopSidebar}
            aria-label={t('sidebar:toggle.expand')}
            title={t('sidebar:toggle.expand')}
          >
            <Menu />
          </button>
        )}

        {!isAccountLimited && (
          !isMobile ? (
            <Sidebar
              desktopCollapsed={desktopSidebarCollapsed}
              onToggleDesktopCollapse={toggleDesktopSidebar}
            />
          ) : (
            <Drawer
              anchor="left"
              onClose={toggleDrawer}
              open={drawerVisible}
              className="mobile-drawer"
              sx={{ zIndex: 1100 }}
              slotProps={{
                paper: {
                  sx: {
                    flex: 'none',
                    width: 'min(300px, 86%)',
                    maxWidth: '86%',
                    minWidth: 0,
                    height: '100%',
                    maxHeight: '100%',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    m: 0,
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    borderRadius: '0 24px 24px 0',
                    background: '#ffffff',
                    boxShadow: '8px 0 32px rgba(0, 0, 0, 0.18)',
                  },
                },
              }}
            >
              <Sidebar variant="drawer" />
            </Drawer>
          )
        )}

        {/* Conteneur droit, qui va accueillir le contenu et le footer */}
        <div className="private-layout-right" ref={layoutRightRef}>
          {/* Header Desktop avec cloche de notification */}
          {!isMobile && !isAccountLimited && (
            <div className="desktop-header">
              <DesktopHeader />
            </div>
          )}

          <div className="private-layout-content">
            {/* Onboarding: le modal n'est plus global; la checklist n'est plus forcée ici */}
            {/* Le contenu des pages privées (Routes enfants) se rend ici */}
            {isAccountLimited ? (
              <div className="account-limited-placeholder" aria-hidden="true">
                <div>
                  <p className="account-limited-eyebrow">{t('common:accountLimited.eyebrow')}</p>
                  <h1>{t('common:accountLimited.title')}</h1>
                  <p>{t('common:accountLimited.body')}</p>
                </div>
              </div>
            ) : (
              <Suspense fallback={<RouteLazyFallback />}>
                <PushNotificationPrompt />
                <Outlet />
              </Suspense>
            )}
          </div>
        </div>

        {/* Footer mobile */}
        {isMobile && !isAccountLimited && <MobileFooter onAddClick={handleMobileFooterAddClick} />}

        {/* Drawer Programmer mobile : ouvert depuis n'importe quel onglet via le bouton central */}
        {isMobile && !isAccountLimited && (
          <Drawer
            anchor="bottom"
            onClose={() => setProgrammerDrawerOpen(false)}
            open={programmerDrawerOpen}
            sx={{ zIndex: 1100 }}
            slotProps={{
              paper: {
                sx: {
                  borderTopLeftRadius: 16,
                  borderTopRightRadius: 16,
                  height: 'auto',
                  maxHeight: '85vh',
                  p: 2,
                  pb: 'calc(24px + 80px + env(safe-area-inset-bottom, 0px))',
                },
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                {t('buttons.schedule')}
              </Typography>
              <IconButton
                aria-label="Fermer"
                onClick={() => setProgrammerDrawerOpen(false)}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {PROGRAMMER_MENU_ITEMS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="private-layout-programmer-btn"
                  onClick={() => handleProgrammerAction(item.key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '14px 16px',
                    border: '1px solid #e8e8e8',
                    borderRadius: 12,
                    background: '#fff',
                    fontSize: 16,
                    fontWeight: 500,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <item.Icon style={{ color: '#A5765C' }} />
                  {t(item.labelKey)}
                </button>
              ))}
            </div>
          </Drawer>
        )}
        {!isAccountLimited && <WhatsNewEpic4Modal />}
        <AccountRestrictionModal restriction={accountRestriction} onLogout={logout} />
      </div>
  );

  return proIdentity ? (
    <TwilioConversationsProvider identity={proIdentity}>{layoutTree}</TwilioConversationsProvider>
  ) : (
    layoutTree
  );
};

export default PrivateLayout;
